/**
 * MID 下发：把通道账户绑定给具体商户。
 *
 * 这是整个控制台**最需要卡死权限**的动作 —— 一次错误的下发等于把别人的通道
 * 交给一个不该用它的商户去跑真实交易。后端要求同时满足三件事（都在同一个事务里、都加锁）：
 *
 * 1. 你是该商户的**严格祖先**
 * 2. 你**持有该通道的授权**（平台是通道所有者，不需要）
 * 3. 随下发一起提交的费率，其加价落在授权上限内、且不低于你自己的底价
 *
 * 界面上把这三条写出来，是因为被拒时用户需要知道到底卡在哪一条。
 *
 * 停用用的是 DELETE 语义但**不物理删除**：绑定是历史交易的上下文，
 * 删掉之后再看老订单就查不到「当时是谁把这个通道给他的」。
 */

import { useState } from 'react';
import { request, type Collection } from '../lib/api.ts';
import { useAsync, usePagedList, useMutation } from '../lib/hooks.ts';
import { useSession } from '../lib/session.tsx';
import {
  Button, Card, CopyId, ErrorBox, Field, MinorAmount, Modal, Select, StatusBadge, TextInput,
} from '../components/ui.tsx';
import { DataTable, Pager, type Column } from '../components/table.tsx';
import { formatBps } from '../lib/money.ts';
import { formatDateTime } from '../lib/format.ts';
import type { Binding, Channel, Org } from '../lib/types.ts';

export function BindingsPage() {
  const session = useSession();
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Binding | null>(null);

  const list = usePagedList<Binding>('/v1/bindings', {
    status: status === '' ? null : status,
  });

  const canManage = session.can('admin') && session.me.org.status === 'active';

  const columns: readonly Column<Binding>[] = [
    { key: 'merchant', header: '商户', render: (row) => (
      <div className="stack tight">
        <span className="cell-strong">{row.merchant_name ?? '—'}</span>
        <CopyId id={row.merchant_org_id} />
      </div>
    ) },
    { key: 'channel', header: '通道', render: (row) => (
      <div className="stack tight">
        <span className="cell-strong">{row.channel_code ?? '—'}</span>
        <span className="cell-muted" style={{ fontSize: 11 }}>{row.channel_label ?? ''}</span>
      </div>
    ) },
    { key: 'submid', header: '子商户号', render: (row) => (
      row.sub_mid === null
        ? <span className="cell-muted">未设置</span>
        : <span className="mono cell-strong">{row.sub_mid}</span>
    ) },
    { key: 'status', header: '状态', render: (row) => <StatusBadge status={row.status} /> },
    { key: 'override', header: '优先级 / 权重覆盖', numeric: true, render: (row) => (
      <span className="num cell-muted">
        {row.override_priority ?? '—'} / {row.override_weight ?? '—'}
      </span>
    ) },
    // 绑定不携带币种，限额跟随通道 —— 不猜，原样显示最小单位
    { key: 'cap', header: '日限额覆盖', numeric: true, render: (row) => (
      row.cap_daily_override === null
        ? <span className="cell-muted">继承通道</span>
        : <MinorAmount value={row.cap_daily_override} currencies={[]} />
    ) },
    { key: 'granted', header: '下发方', render: (row) => <CopyId id={row.granted_by_org_id} /> },
    { key: 'created', header: '下发时间', render: (row) => (
      <span className="cell-muted mono">{formatDateTime(row.created_at)}</span>
    ) },
    ...(canManage ? [{
      key: 'actions',
      header: '',
      render: (row: Binding) => (
        <Button size="sm" onClick={() => { setEditing(row); }}>调整</Button>
      ),
    }] : []),
  ];

  return (
    <>
      <div className="row">
        <h1>MID 下发</h1>
        <span style={{ flex: 1 }} />
        {canManage && <Button variant="primary" onClick={() => { setCreating(true); }}>下发通道</Button>}
      </div>

      <div className="filters">
        <Field label="状态">
          <Select
            value={status}
            onChange={setStatus}
            placeholder="全部"
            options={[
              { value: 'active', label: '启用' },
              { value: 'paused', label: '暂停' },
              { value: 'disabled', label: '停用' },
            ]}
          />
        </Field>
      </div>

      <Card flush>
        <DataTable
          columns={columns}
          rows={list.items}
          loading={list.loading}
          error={list.error}
          rowKey={(row) => row.id}
          empty="还没有下发任何 MID"
          emptyHint="下发需要同时满足：你是该商户的严格上级、你持有该通道的授权、随附费率的加价在授权上限内。"
        />
        <Pager
          page={list.page}
          hasNext={list.hasNext}
          hasPrev={list.hasPrev}
          onNext={list.next}
          onPrev={list.prev}
          loading={list.loading}
          count={list.items.length}
        />
      </Card>

      {creating && (
        <CreateBindingModal
          onClose={() => { setCreating(false); }}
          onDone={() => { setCreating(false); list.reload(); }}
        />
      )}
      {editing !== null && (
        <EditBindingModal
          binding={editing}
          onClose={() => { setEditing(null); }}
          onDone={() => { setEditing(null); list.reload(); }}
        />
      )}
    </>
  );
}

function CreateBindingModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [merchantId, setMerchantId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [subMid, setSubMid] = useState('');
  const [withFee, setWithFee] = useState(true);
  const [rateBps, setRateBps] = useState('300');
  const [fixedMinor, setFixedMinor] = useState('30');
  const [currency, setCurrency] = useState('USD');

  const merchants = useAsync<{ data: readonly Org[] }>(
    '/v1/orgs',
    { query: { type: 'merchant', status: 'active', limit: 200 } },
  );
  const channels = useAsync<Collection<Channel>>('/v1/channels', { query: { status: 'active' } });

  const create = useMutation(async () => await request<Binding>('/v1/bindings', {
    method: 'POST',
    body: {
      merchant_org_id: merchantId,
      channel_account_id: channelId,
      ...(subMid === '' ? {} : { sub_mid: subMid }),
      ...(withFee
        ? {
            fee: {
              rate_bps: Number(rateBps),
              fixed_minor: fixedMinor,
              ...(currency === '' ? {} : { currency: currency.toUpperCase() }),
            },
          }
        : {}),
    },
  }));

  return (
    <Modal
      title="下发通道给商户"
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            disabled={create.pending || merchantId === '' || channelId === ''}
            onClick={() => { void create.run().then((result) => { if (result !== null) onDone(); }); }}
          >
            {create.pending ? '下发中…' : '下发'}
          </Button>
        </>
      }
    >
      <ErrorBox error={create.error} />

      <div className="grid cols-2">
        <Field label="商户" hint="只能选你管得到的商户（严格下级）。">
          <Select
            value={merchantId}
            onChange={setMerchantId}
            placeholder="选择商户"
            options={(merchants.data?.data ?? []).map((org) => ({ value: org.id, label: org.name }))}
          />
        </Field>
        <Field label="通道账户" hint="只能选你持有授权的通道。">
          <Select
            value={channelId}
            onChange={setChannelId}
            placeholder="选择通道"
            options={(channels.data?.data ?? []).map((channel) => ({
              value: channel.id,
              label: `${channel.channel_code} · ${channel.label} · ${channel.mode}`,
            }))}
          />
        </Field>
      </div>

      <Field
        label="子商户号（sub MID）"
        hint="通道侧用来区分收款主体的标识。Stripe Connect / Square 这类通道靠它决定钱进谁的户头 —— 填错等于把商户的钱导去别处。"
      >
        <TextInput value={subMid} onChange={setSubMid} placeholder="留空表示不设置" />
      </Field>

      <label className="checkbox">
        <input type="checkbox" checked={withFee} onChange={(e) => { setWithFee(e.target.checked); }} />
        同时配置该商户的费率（强烈建议 —— 没有费率就没法计费）
      </label>

      {withFee && (
        <>
          <div className="grid cols-3">
            <Field label="费率（基点）" hint={formatBps(Number(rateBps) || 0)}>
              <TextInput value={rateBps} onChange={setRateBps} numeric />
            </Field>
            <Field label="固定费（最小单位）" hint="例如 30 = USD 0.30">
              <TextInput value={fixedMinor} onChange={setFixedMinor} numeric />
            </Field>
            <Field label="币种" hint="留空表示对所有币种生效">
              <TextInput value={currency} onChange={setCurrency} />
            </Field>
          </div>

          <div className="alert" data-tone="info">
            <span className="alert-title">加价会被整体校验</span>
            <span>
              后端拿这四个参数（费率、固定费、封底、封顶）一起算出相对你自己底价的加价，
              超过授权上限、或低于你的底价，都会被拒绝。低于底价等于拿平台的钱补贴自己的客户。
            </span>
          </div>
        </>
      )}
    </Modal>
  );
}

function EditBindingModal({
  binding, onClose, onDone,
}: {
  binding: Binding;
  onClose: () => void;
  onDone: () => void;
}) {
  const [status, setStatus] = useState(binding.status);
  const [subMid, setSubMid] = useState(binding.sub_mid ?? '');

  const save = useMutation(async () => await request<Binding>(`/v1/bindings/${binding.id}`, {
    method: 'PATCH',
    body: {
      ...(status !== binding.status ? { status } : {}),
      ...(subMid !== (binding.sub_mid ?? '') ? { sub_mid: subMid === '' ? null : subMid } : {}),
    },
  }));

  const disable = useMutation(async () => await request<Binding>(`/v1/bindings/${binding.id}`, {
    method: 'DELETE',
  }));

  const dirty = status !== binding.status || subMid !== (binding.sub_mid ?? '');

  return (
    <Modal
      title="调整绑定"
      onClose={onClose}
      footer={
        <>
          <Button
            variant="danger"
            disabled={disable.pending || binding.status === 'disabled'}
            onClick={() => { void disable.run().then((result) => { if (result !== null) onDone(); }); }}
          >
            停用
          </Button>
          <span style={{ flex: 1 }} />
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            disabled={save.pending || !dirty}
            onClick={() => { void save.run().then((result) => { if (result !== null) onDone(); }); }}
          >
            {save.pending ? '保存中…' : '保存'}
          </Button>
        </>
      }
    >
      <ErrorBox error={save.error} />
      <ErrorBox error={disable.error} />

      <p className="field-hint">
        {binding.merchant_name ?? binding.merchant_org_id} · {binding.channel_code ?? binding.channel_account_id}
      </p>

      <Field label="状态">
        <Select
          value={status}
          onChange={(value) => { setStatus(value as Binding['status']); }}
          options={[
            { value: 'active', label: '启用' },
            { value: 'paused', label: '暂停' },
            { value: 'disabled', label: '停用' },
          ]}
        />
      </Field>

      <Field label="子商户号">
        <TextInput value={subMid} onChange={setSubMid} placeholder="留空表示清除" />
      </Field>

      <div className="alert" data-tone="warn">
        <span className="alert-title">改这条绑定同样需要该通道的授权</span>
        <span>
          「管得到这个商户」不等于「有权动这个通道」。平台可以直接给商户下发 MID，
          此时链路上的代理并没有该通道的授权 —— 后端会挡住它改 sub_mid 或抬高限额。
        </span>
      </div>

      <div className="alert" data-tone="info">
        <span className="alert-title">停用不是删除</span>
        <span>绑定是历史交易的上下文，删掉之后再看老订单就查不到当时是谁下发的这个通道。</span>
      </div>
    </Modal>
  );
}
